/**
 * Unit Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useUnitStore } from '@/store/unit.store';
import { useTenantStore } from '@/store/tenant.store';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { Logger } from '@/lib/app-log.js';

export class UnitIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /** Lookup list without mutating the unit admin store or toasting. */
    async queryAll(params = {}) {
        return this.service.getAll(params);
    }

    /**
     * Fetch units and update store
     */
    async fetchUnits(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useUnitStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                columnFilters
            });

            const { data, pagination, uniqueValues } = response;
            store.setUnits(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Full list for hierarchy graph only — does not mutate unit store (avoids clobbering table pagination).
     */
    async fetchUnitsForHierarchyGraph() {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.getAll({
                page: 1,
                pageSize: 10000,
                search: '',
                sortBy: 'name',
                sortOrder: 'asc',
                tenantId,
                columnFilters: {},
            });
            return { success: true, data: response.data || [] };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, data: [], error: errorMessage };
        }
    }

    /**
     * Fetch a single unit by id (for notification highlight locate).
     */
    async fetchUnitById(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !id) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data ?? response };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /**
     * Jump to the pagination page containing the entity (default list: createdAt desc, no search/filters).
     */
    async locateUnitInList({ entityId, pageSize = 10 }) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !entityId) {
            return { found: false };
        }

        try {
            const locateRes = await this.service.locateInList({
                focusEntityId: String(entityId),
                pageSize,
                tenantId,
                sortBy: '',
                sortOrder: 'desc',
            });

            const focus = locateRes.focus;
            if (!focus?.found || !focus.page) {
                return { found: false };
            }

            const page = focus.page;
            const res = await this.fetchUnits(page, pageSize, '', '', 'desc', {});
            if (!res.success) {
                return { found: false };
            }

            return { found: true, page };
        } catch (error) {
            Logger.error('locateUnitInList failed', { error: error?.message });
            return { found: false };
        }
    }

    /**
     * Fetch units for dropdown
     */
    async fetchUnitsForDropdown(options = {}) {
        const store = useUnitStore.getState();
        const requestTenantId = options.tenantId || useTenantStore.getState().selectedTenantId;
        try {
            const result = await fetchDropdownOptionsFromReference('unit', async () => {
                if (this.service.getDropdownParams) {
                    const response = await this.service.getDropdownParams();
                    return { success: true, data: response.data || [] };
                }
                const response = await this.service.getAll({ pageSize: 1000 });
                const data = response.data || [];
                return {
                    success: true,
                    data: data.map((dept) => ({
                        value: dept._id || dept.id,
                        label: dept.name || dept.unitName || 'Unnamed Unit',
                    })),
                };
            }, options);
            if (String(useTenantStore.getState().selectedTenantId || '') !== String(requestTenantId || '')) return;
            store.setUnitsForDropdown(result.data || []);
        } catch (error) {
            Logger.error('Failed to fetch unit dropdown', { error: error?.message });
        }
    }

    /**
     * Create unit
     */
    async createUnit(data) {
        const store = useUnitStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            toast.success(i18next.t('integrationToasts:unitCreated', { defaultValue: 'Unit created' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update unit
     */
    async updateUnit(id, data) {
        const store = useUnitStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:unitUpdated', { defaultValue: 'Unit updated' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete unit
     */
    async deleteUnit(id) {
        const store = useUnitStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:unitDeleted', { defaultValue: 'Unit deleted' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete units
     * @param {Array<string>} ids
     */
    async bulkDeleteUnits(ids) {
        const store = useUnitStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('integrationToasts:unitDeleted', { defaultValue: 'Unit deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Transfer units to new parent
     */
    async transferUnits(data) {
        const store = useUnitStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.transfer(data);

            toast.success(i18next.t('integrationToasts:unitTransferred', { defaultValue: 'Unit transferred' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async assignUsersToUnit(unitId, userIds) {
        try {
            await this.service.assignUsers(unitId, userIds);
            toast.success(i18next.t('integrationToasts:usersAssignedToUnit', { defaultValue: 'Users assigned to unit' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async unassignUserFromUnit(unitId, userId) {
        try {
            await this.service.unassignUser(unitId, userId);
            toast.success(i18next.t('integrationToasts:userRemovedFromUnit', { defaultValue: 'User removed from unit' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async fetchUnitUserCounts() {
        try {
            const response = await this.service.getUserCounts();
            return {
                success: true,
                data: response.data || [],
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

}
